export default `<p><strong>Versión del documento:</strong> V3.28 &nbsp;|&nbsp; <strong>Fecha de publicación:</strong> 2022 &nbsp;|&nbsp; <strong>Tipo:</strong> Whitepaper del producto y resumen de evaluación de seguridad</p>

<h2>1. Resumen ejecutivo</h2>
<p>Este módulo Lending Pool es un servicio de préstamo y liquidez on-chain basado en protocolos DeFi de mercado monetario. Mediante smart contracts de código abierto y modelos de tipos programáticos, el sistema conecta los activos digitales de los usuarios con mercados de préstamo on-chain consolidados, permitiendo <strong>rendimiento en depósitos, préstamos colateralizados y liquidez compartida</strong>.</p>
<p>El módulo no depende de intervención manual para la asignación diaria de fondos. Las tasas, capacidades y utilización se determinan en tiempo real por los mercados on-chain y se muestran de forma transparente mediante agregación de datos multi-fuente.</p>

<h2>2. Contexto del sector y posicionamiento</h2>
<p>El crédito tradicional depende de instituciones centralizadas. Los protocolos DeFi utilizan smart contracts para emparejar automáticamente oferta y demanda, con sobrecolateralización y mecanismos de liquidación.</p>
<p>Posicionamiento de este módulo:</p>
<ul>
  <li><strong>Puerta de entrada a mercados monetarios on-chain:</strong> agrega datos de pools en múltiples cadenas y protocolos;</li>
  <li><strong>Distribución programática de rendimientos:</strong> liquidación automática según calendario fijo basado en saldos autorizados y APY de mercado;</li>
  <li><strong>Transparente y verificable:</strong> TVL, APY, utilización y LTV trazables a datos on-chain públicos;</li>
  <li><strong>No custodial:</strong> la autorización e interacciones on-chain se inician desde la wallet del usuario.</li>
</ul>

<h2>3. Arquitectura del sistema</h2>
<h3>3.1 Visión general</h3>
<ul>
  <li><strong>Capa de interfaz:</strong> conexión de wallet, autorización, exploración de pools, consulta de ganancias;</li>
  <li><strong>Capa de servicios:</strong> autenticación, gestión de autorizaciones, órdenes de rendimiento, contenido multilingüe;</li>
  <li><strong>Capa de agregación:</strong> sincronización programada desde fuentes DeFi públicas;</li>
  <li><strong>Capa de protocolo on-chain:</strong> smart contracts de préstamo auditados ejecutan depósitos y préstamos.</li>
</ul>

<h3>3.2 Métricas clave</h3>
<table>
  <thead>
    <tr><th>Métrica</th><th>Descripción</th></tr>
  </thead>
  <tbody>
    <tr><td>TVL</td><td>Activos totales del pool en USD</td></tr>
    <tr><td>Supply APY</td><td>Rendimiento anual de depósitos (tasa base + recompensas)</td></tr>
    <tr><td>Borrow APY</td><td>Coste anual pagado por prestatarios</td></tr>
    <tr><td>Tasa de utilización</td><td>Proporción de fondos prestados sobre disponibles</td></tr>
    <tr><td>Max LTV</td><td>Ratio préstamo/valor máximo antes de liquidación</td></tr>
    <tr><td>Supply / Borrow Cap</td><td>Límites por activo para controlar riesgo de concentración</td></tr>
    <tr><td>Liquidez disponible</td><td>Liquidez inmediatamente prestable</td></tr>
  </tbody>
</table>

<h2>4. Flujo de usuario y mecánica de rendimiento</h2>
<ol>
  <li><strong>Conectar wallet</strong> vía Web3;</li>
  <li><strong>Autorizar activos</strong> (USDT, USDC, etc.) con Approve on-chain;</li>
  <li><strong>Unirse al lending pool</strong> para entrar en la cola de cálculo de rendimientos;</li>
  <li><strong>Liquidación de rendimientos</strong> diaria según saldo autorizado y tasas de mercado;</li>
  <li><strong>Consultar activos</strong> en «Mi cartera».</li>
</ol>
<p><strong>El APY histórico no garantiza rendimientos futuros.</strong></p>

<h3>4.3 Comisiones</h3>
<h4>¿Cómo se cobran las comisiones de DeFi Earn?</h4>
<p>Las transacciones on-chain requieren comisiones de gas para interactuar con smart contracts, incluida la suscripción y el cobro de rendimientos. Para ciertos activos, el lending pool DeFi y los gestores de vault pueden cobrar una comisión de rendimiento como porcentaje de sus beneficios reales; el capital no se ve afectado.</p>

<h2>5. Diseño de seguridad</h2>
<ul>
  <li>Protocolos subyacentes con contratos auditados de código abierto;</li>
  <li>Arquitectura no custodial; sin almacenamiento de claves privadas;</li>
  <li>Permisos Token Approve mínimos; revocables en cualquier momento;</li>
  <li>Auth por token, HTTPS, RBAC administrativo y registros de auditoría.</li>
</ul>

<h2>6. Resumen de evaluación de seguridad</h2>
<table>
  <thead>
    <tr><th>Elemento</th><th>Resultado</th><th>Notas</th></tr>
  </thead>
  <tbody>
    <tr><td>Dependencias de smart contract</td><td><strong>Aprobado</strong></td><td>Protocolos DeFi auditados públicamente</td></tr>
    <tr><td>No custodial y gestión de claves</td><td><strong>Aprobado</strong></td><td>Sin almacenamiento de claves privadas</td></tr>
    <tr><td>API y autenticación</td><td><strong>Aprobado</strong></td><td>Auth token y HTTPS implementados</td></tr>
    <tr><td>Sincronización de datos</td><td><strong>Aprobado</strong></td><td>Sync programada con snapshots</td></tr>
    <tr><td>Lógica de liquidación de rendimientos</td><td><strong>Aprobado</strong></td><td>Reglas explícitas; órdenes auditables</td></tr>
  </tbody>
</table>

<h2>7. Divulgación de riesgos</h2>
<ul>
  <li><strong>Riesgo de smart contract:</strong> pueden existir vulnerabilidades no descubiertas;</li>
  <li><strong>Riesgo de mercado:</strong> el APY puede fluctuar significativamente;</li>
  <li><strong>Riesgo de liquidación:</strong> caídas del colateral pueden activar liquidación;</li>
  <li><strong>Riesgo de de-peg de stablecoins:</strong> USDT/USDC pueden desviarse de $1,00;</li>
  <li><strong>Riesgo regulatorio:</strong> la regulación DeFi evoluciona globalmente.</li>
</ul>
<p><strong>Este módulo no proporciona asesoramiento de inversión ni garantiza rendimientos fijos.</strong></p>

<h2>8. Glosario</h2>
<ul>
  <li><strong>APY:</strong> rendimiento porcentual anual;</li>
  <li><strong>TVL:</strong> valor total bloqueado;</li>
  <li><strong>LTV:</strong> ratio préstamo/valor;</li>
  <li><strong>Approve:</strong> autorización de token ERC-20;</li>
  <li><strong>Liquidación:</strong> venta automática de colateral por subcolateralización.</li>
</ul>

<p>— Solo con fines informativos. No constituye asesoramiento legal, financiero o de inversión. Verifique los datos on-chain mediante exploradores de bloques e interfaces oficiales de protocolos.</p>`
